import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Stack,
  Divider,
  Alert,
  Grid,
  Fab,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowDownward as ArrowDownwardIcon,
  ArrowUpward as ArrowUpwardIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { 
  PaymentTransaction, 
  PaymentTransactionType, 
  getPaymentTypeDisplayName, 
  getPaymentTypeColor 
} from '@/types/paymentTransaction';
import PaymentTransactionDialog, { PaymentTransactionFormData } from './PaymentTransactionDialog';
import { paymentTransactionService } from '@/services/paymentTransactionService';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/hooks/useI18n';

interface PaymentTransactionManagerProps {
  tripId: string;
  billId: string;
  transactions: PaymentTransaction[];
  billGrandTotal: number;
  currentPaidAmount: number;
  onUpdate: () => void;
}

const PaymentTransactionManager: React.FC<PaymentTransactionManagerProps> = ({
  tripId,
  billId,
  transactions,
  billGrandTotal,
  currentPaidAmount,
  onUpdate
}) => {
  const { user } = useAuth();
  const { t } = useI18n();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [selectedTransactionType, setSelectedTransactionType] = useState<PaymentTransactionType>(PaymentTransactionType.RECEIVED);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Separate transactions by type
  const receivedTransactions = transactions.filter(t => t.type === PaymentTransactionType.RECEIVED);
  const sentTransactions = transactions.filter(t => t.type === PaymentTransactionType.SENT);

  const totalReceived = paymentTransactionService.getTotalReceived({ paymentTransactions: transactions });
  const totalSent = paymentTransactionService.getTotalSent({ paymentTransactions: transactions });

  const handleAddTransaction = async (transactionData: PaymentTransactionFormData) => {
    if (!user) return;

    try {
      setIsLoading(true);
      setError(null);

      if (selectedTransactionType === PaymentTransactionType.RECEIVED) {
        await paymentTransactionService.createPaymentReceived(
          tripId,
          billId,
          transactionData.amount,
          undefined, // personId - we don't have person IDs yet
          transactionData.personName,
          transactionData.reference,
          transactionData.paymentMethod,
          transactionData.description,
          user.uid,
          user.displayName || undefined
        );
      } else {
        await paymentTransactionService.createPaymentSent(
          tripId,
          billId,
          transactionData.amount,
          transactionData.personName, // recipient
          undefined, // personId
          transactionData.personName,
          transactionData.reference,
          transactionData.paymentMethod,
          transactionData.description,
          user.uid,
          user.displayName || undefined
        );
      }

      setIsAddDialogOpen(false);
      onUpdate();
    } catch (error) {
      console.error('Error adding payment transaction:', error);
      setError('Fehler beim Hinzufügen der Zahlung');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteTransaction = async (transactionId: string) => {
    try {
      setIsLoading(true);
      setError(null);
      await paymentTransactionService.deletePaymentTransaction(tripId, billId, transactionId);
      onUpdate();
    } catch (error) {
      console.error('Error deleting payment transaction:', error);
      setError('Fehler beim Löschen der Zahlung');
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatDateTime = (date: Date): string => {
    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const renderTransactionList = (transactionList: PaymentTransaction[], type: PaymentTransactionType) => {
    if (transactionList.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
          Keine {type === PaymentTransactionType.RECEIVED ? 'Eingänge' : 'Ausgänge'} vorhanden
        </Typography>
      );
    }

    return (
      <List dense>
        {transactionList.map((transaction, index) => (
          <React.Fragment key={transaction.id}>
            <ListItem>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle2" component="span">
                      {formatCurrency(transaction.amount)}
                    </Typography>
                    <Chip
                      size="small"
                      icon={type === PaymentTransactionType.RECEIVED ? <ArrowDownwardIcon /> : <ArrowUpwardIcon />}
                      label={getPaymentTypeDisplayName(type)}
                      color={getPaymentTypeColor(type)}
                      variant="outlined"
                    />
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {transaction.personName && `Person: ${transaction.personName}`}
                      {transaction.paymentMethod && ` • ${transaction.paymentMethod}`}
                    </Typography>
                    {transaction.reference && (
                      <Typography variant="body2" color="text.secondary">
                        Referenz: {transaction.reference}
                      </Typography>
                    )}
                    {transaction.description && (
                      <Typography variant="body2" color="text.secondary">
                        {transaction.description}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {formatDateTime(transaction.timestamp)}
                    </Typography>
                  </Box>
                }
              />
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  aria-label="delete"
                  onClick={() => handleDeleteTransaction(transaction.id)}
                  disabled={isLoading}
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
            {index < transactionList.length - 1 && <Divider component="li" />}
          </React.Fragment>
        ))}
      </List>
    );
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Summary */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title={t('bill.paymentOverview')}
          subheader={t('bill.paymentTransactionOverview')}
        />
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" color="primary">
                  {formatCurrency(billGrandTotal)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Gesamtbetrag
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" color="success.main">
                  {formatCurrency(totalReceived)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Eingänge ({receivedTransactions.length})
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" color="error.main">
                  {formatCurrency(totalSent)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Ausgänge ({sentTransactions.length})
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" color={currentPaidAmount >= billGrandTotal ? 'success.main' : 'warning.main'}>
                  {formatCurrency(billGrandTotal - currentPaidAmount)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Verbleibend
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Payment Received */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ArrowDownwardIcon color="success" />
                  <Typography variant="h6">Zahlungseingänge</Typography>
                </Box>
              }
              action={
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setSelectedTransactionType(PaymentTransactionType.RECEIVED);
                    setIsAddDialogOpen(true);
                  }}
                  color="success"
                  variant="outlined"
                >
                  Hinzufügen
                </Button>
              }
            />
            <CardContent sx={{ pt: 0 }}>
              {renderTransactionList(receivedTransactions, PaymentTransactionType.RECEIVED)}
            </CardContent>
          </Card>
        </Grid>

        {/* Payment Sent */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ArrowUpwardIcon color="error" />
                  <Typography variant="h6">Zahlungsausgänge</Typography>
                </Box>
              }
              action={
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setSelectedTransactionType(PaymentTransactionType.SENT);
                    setIsAddDialogOpen(true);
                  }}
                  color="error"
                  variant="outlined"
                >
                  Hinzufügen
                </Button>
              }
            />
            <CardContent sx={{ pt: 0 }}>
              {renderTransactionList(sentTransactions, PaymentTransactionType.SENT)}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Add Transaction Dialog */}
      <PaymentTransactionDialog
        open={isAddDialogOpen}
        onClose={() => setIsAddDialogOpen(false)}
        onConfirm={handleAddTransaction}
        type={selectedTransactionType}
        isLoading={isLoading}
        error={error}
        billGrandTotal={billGrandTotal}
        currentPaidAmount={currentPaidAmount}
      />
    </Box>
  );
};

export default PaymentTransactionManager;